import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { ApiError } from "../api/client.ts";
import {
  getCalendarDay,
  rescheduleJob,
  type ConflictItem,
  type RescheduleResult,
} from "../api/calendar.ts";
import type { Job } from "../api/jobs.ts";

// One slot = 30 minutes. Each slot is rendered with this CSS height (px).
const SLOT_PX = 28;
const MINUTES_PER_SLOT = 30;
const SLOTS_PER_HOUR = 60 / MINUTES_PER_SLOT;

const STATUS_COLOR: Record<string, string> = {
  new: "bg-slate-100 border-slate-300 text-slate-700",
  scheduled: "bg-blue-100 border-blue-300 text-blue-800",
  en_route: "bg-amber-100 border-amber-300 text-amber-900",
  on_site: "bg-emerald-100 border-emerald-300 text-emerald-900",
  completed: "bg-emerald-50 border-emerald-200 text-emerald-700 line-through",
  cancelled: "bg-red-50 border-red-200 text-red-600 line-through",
};

function todayISO(): string {
  const d = new Date();
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())).toISOString().slice(0, 10);
}

function shiftDate(iso: string, days: number): string {
  const d = new Date(iso + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function slotIndexFromDate(iso: string, dt: Date, openTime: string | null): number {
  const dayStart = new Date(iso + "T00:00:00Z");
  const openOffsetMin = openTime ? hhmmToMinutes(openTime) : 0;
  const minsFromMidnight = (dt.getTime() - dayStart.getTime()) / 60_000;
  return Math.max(0, Math.floor((minsFromMidnight - openOffsetMin) / MINUTES_PER_SLOT));
}

function hhmmToMinutes(t: string): number {
  const [h, m] = t.split(":").map(Number);
  return h * 60 + (m || 0);
}

function slotIndexToDate(iso: string, idx: number, openTime: string | null): Date {
  const openOffsetMin = openTime ? hhmmToMinutes(openTime) : 0;
  const totalMin = openOffsetMin + idx * MINUTES_PER_SLOT;
  const d = new Date(iso + "T00:00:00Z");
  d.setUTCMinutes(totalMin);
  return d;
}

function jobDurationMinutes(job: Job): number {
  if (job.scheduled_start && job.scheduled_end) {
    return Math.max(MINUTES_PER_SLOT, Math.round(
      (new Date(job.scheduled_end).getTime() - new Date(job.scheduled_start).getTime()) / 60_000,
    ));
  }
  return Math.max(MINUTES_PER_SLOT, job.duration_estimate_minutes || 60);
}

interface DragPayload {
  job_id: string;
  duration_minutes: number;
  source_lane_id: string | "unassigned";
}

export default function CalendarPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [date, setDate] = useState<string>(todayISO());
  const [conflictPrompt, setConflictPrompt] = useState<{
    job_id: string;
    technician_id?: string;
    scheduled_start: string;
    scheduled_end: string;
    conflicts: ConflictItem[];
  } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["calendar", date],
    queryFn: () => getCalendarDay({ date }),
  });

  const reschedule = useMutation({
    mutationFn: (vars: { id: string; technician_id?: string; scheduled_start: string; scheduled_end: string; force?: boolean }) =>
      rescheduleJob(vars.id, {
        technician_id: vars.technician_id,
        scheduled_start: vars.scheduled_start,
        scheduled_end: vars.scheduled_end,
        force: vars.force,
      }),
    onSuccess: (_data: RescheduleResult) => {
      qc.invalidateQueries({ queryKey: ["calendar", date] });
      qc.invalidateQueries({ queryKey: ["jobs"] });
      setConflictPrompt(null);
    },
    onError: (err: ApiError, vars) => {
      const detail = (err.detail as { detail?: { conflicts?: ConflictItem[] } } | undefined)?.detail;
      const conflicts = detail?.conflicts;
      if (err.status === 409 && conflicts) {
        setConflictPrompt({
          job_id: vars.id,
          technician_id: vars.technician_id,
          scheduled_start: vars.scheduled_start,
          scheduled_end: vars.scheduled_end,
          conflicts,
        });
      }
    },
  });

  // Compute slot grid bounds. Default to 08:00–18:00 if no business hours.
  const { openTime, totalSlots } = useMemo(() => {
    const open = data?.open_time ?? "08:00:00";
    const close = data?.close_time ?? "18:00:00";
    const minutes = hhmmToMinutes(close) - hhmmToMinutes(open);
    return {
      openTime: open,
      totalSlots: Math.max(1, Math.ceil(minutes / MINUTES_PER_SLOT)),
    };
  }, [data]);

  const handleDrop = (laneId: string | undefined, slotIdx: number, payload: DragPayload) => {
    const start = slotIndexToDate(date, slotIdx, openTime);
    const end = new Date(start.getTime() + payload.duration_minutes * 60_000);
    reschedule.mutate({
      id: payload.job_id,
      technician_id: laneId,
      scheduled_start: start.toISOString(),
      scheduled_end: end.toISOString(),
      force: false,
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold">{t("calendar.title")}</h1>
        <div className="flex items-center gap-2">
          <button type="button" className="btn-secondary" onClick={() => setDate(shiftDate(date, -1))}>
            <ChevronLeft className="h-4 w-4" />
          </button>
          <input
            type="date"
            className="input"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
          <button type="button" className="btn-secondary" onClick={() => setDate(shiftDate(date, 1))}>
            <ChevronRight className="h-4 w-4" />
          </button>
          <button type="button" className="btn-secondary" onClick={() => setDate(todayISO())}>
            {t("calendar.today")}
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : data ? (
        <div className="card overflow-x-auto p-0">
          <div className="grid" style={{ gridTemplateColumns: `120px repeat(${data.lanes.length || 1}, minmax(180px, 1fr))` }}>
            {/* Header row */}
            <div className="border-b border-r border-slate-200 bg-slate-50 px-2 py-2 text-xs uppercase text-slate-500">
              {t("calendar.time")}
            </div>
            {data.lanes.map((lane) => (
              <div key={lane.user_id} className="border-b border-r border-slate-200 bg-slate-50 px-2 py-2 text-sm font-medium">
                {lane.full_name}
              </div>
            ))}

            {/* Time grid */}
            <TimeColumn totalSlots={totalSlots} openTime={openTime} />
            {data.lanes.map((lane) => (
              <LaneColumn
                key={lane.user_id}
                lane={lane}
                date={date}
                openTime={openTime}
                totalSlots={totalSlots}
                onDrop={(slotIdx, payload) => handleDrop(lane.user_id, slotIdx, payload)}
              />
            ))}
          </div>
          <div className="border-t border-slate-200 bg-slate-50 p-3">
            <div className="mb-1 text-xs uppercase tracking-wide text-slate-500">{t("calendar.unassigned")}</div>
            {data.unassigned.length === 0 ? (
              <div className="text-xs text-slate-400">—</div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {data.unassigned.map((j) => (
                  <DraggableJob key={j.id} job={j} sourceLaneId="unassigned" />
                ))}
              </div>
            )}
          </div>
          <div className="border-t border-slate-200 px-3 py-2 text-xs text-slate-500">
            {data.open_time
              ? <>{t("calendar.businessHours")}: {data.open_time.slice(0,5)}–{data.close_time?.slice(0,5)} {data.timezone}</>
              : t("calendar.noBusinessHours")}
          </div>
        </div>
      ) : null}

      {conflictPrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setConflictPrompt(null)}>
          <div className="card max-w-md space-y-3" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-red-700">{t("calendar.conflict.title")}</h3>
            <p className="text-sm text-slate-600">{t("calendar.conflict.message")}</p>
            <ul className="space-y-1 text-sm">
              {conflictPrompt.conflicts.map((c) => (
                <li key={c.job_id} className="rounded border border-red-200 bg-red-50 p-2">
                  <span className="font-mono text-xs text-slate-500">{c.job_number}</span>{" "}
                  <span className="font-medium">{c.title}</span>{" — "}
                  <span className="text-xs text-slate-600">
                    {new Date(c.scheduled_start).toLocaleTimeString()}–{new Date(c.scheduled_end).toLocaleTimeString()}
                  </span>
                </li>
              ))}
            </ul>
            <div className="flex justify-end gap-2">
              <button type="button" className="btn-secondary" onClick={() => setConflictPrompt(null)}>
                {t("common.cancel")}
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={() => reschedule.mutate({
                  id: conflictPrompt.job_id,
                  technician_id: conflictPrompt.technician_id,
                  scheduled_start: conflictPrompt.scheduled_start,
                  scheduled_end: conflictPrompt.scheduled_end,
                  force: true,
                })}
              >
                {t("calendar.conflict.dropAnyway")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TimeColumn({ totalSlots, openTime }: { totalSlots: number; openTime: string }) {
  const labels: { idx: number; label: string }[] = [];
  for (let i = 0; i < totalSlots; i += SLOTS_PER_HOUR) {
    const m = hhmmToMinutes(openTime) + i * MINUTES_PER_SLOT;
    const hh = String(Math.floor(m / 60)).padStart(2, "0");
    const mm = String(m % 60).padStart(2, "0");
    labels.push({ idx: i, label: `${hh}:${mm}` });
  }
  return (
    <div className="relative border-r border-slate-200 bg-white" style={{ height: totalSlots * SLOT_PX }}>
      {labels.map((l) => (
        <div key={l.idx} className="absolute left-0 right-0 px-2 text-xs text-slate-400" style={{ top: l.idx * SLOT_PX }}>
          {l.label}
        </div>
      ))}
    </div>
  );
}

function LaneColumn({
  lane,
  date,
  openTime,
  totalSlots,
  onDrop,
}: {
  lane: { user_id: string; full_name: string; jobs: Job[] };
  date: string;
  openTime: string;
  totalSlots: number;
  onDrop: (slotIdx: number, payload: DragPayload) => void;
}) {
  const [hoverSlot, setHoverSlot] = useState<number | null>(null);

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setHoverSlot(null);
    const raw =
      e.dataTransfer.getData("application/json") ||
      e.dataTransfer.getData("text/plain");
    if (!raw) {
      console.warn("calendar: drop received with no payload (browser may have stripped it)");
      return;
    }
    let payload: DragPayload;
    try {
      payload = JSON.parse(raw);
    } catch {
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const slotIdx = Math.max(0, Math.min(totalSlots - 1, Math.floor(y / SLOT_PX)));
    onDrop(slotIdx, payload);
  };

  return (
    <div
      className="relative border-r border-slate-200 bg-white"
      style={{ height: totalSlots * SLOT_PX }}
      onDragOver={(e) => {
        e.preventDefault();
        const rect = e.currentTarget.getBoundingClientRect();
        const y = e.clientY - rect.top;
        setHoverSlot(Math.max(0, Math.min(totalSlots - 1, Math.floor(y / SLOT_PX))));
      }}
      onDragLeave={() => setHoverSlot(null)}
      onDrop={handleDrop}
    >
      {/* Hour gridlines */}
      {Array.from({ length: Math.ceil(totalSlots / SLOTS_PER_HOUR) + 1 }).map((_, i) => (
        <div
          key={i}
          className="absolute left-0 right-0 border-t border-slate-100"
          style={{ top: i * SLOTS_PER_HOUR * SLOT_PX }}
        />
      ))}
      {hoverSlot !== null && (
        <div
          className="absolute left-1 right-1 rounded border-2 border-dashed border-brand-400 bg-brand-50/40"
          style={{ top: hoverSlot * SLOT_PX, height: SLOT_PX * 2 }}
        />
      )}
      {lane.jobs.map((j) => (
        <ScheduledJobCard key={j.id} job={j} date={date} openTime={openTime} sourceLaneId={lane.user_id} />
      ))}
    </div>
  );
}

function setDragPayload(e: React.DragEvent, payload: DragPayload) {
  const json = JSON.stringify(payload);
  e.dataTransfer.setData("application/json", json);
  e.dataTransfer.setData("text/plain", json); // belt-and-suspenders for browsers that prefer text
  e.dataTransfer.effectAllowed = "move";
}

function ScheduledJobCard({
  job,
  date,
  openTime,
  sourceLaneId,
}: {
  job: Job;
  date: string;
  openTime: string;
  sourceLaneId: string;
}) {
  const navigate = useNavigate();
  if (!job.scheduled_start) return null;
  const startSlot = slotIndexFromDate(date, new Date(job.scheduled_start), openTime);
  const dur = jobDurationMinutes(job);
  const heightSlots = Math.max(1, Math.round(dur / MINUTES_PER_SLOT));
  const colorClass = STATUS_COLOR[job.status] ?? STATUS_COLOR.new;
  const dragStarted = { current: false } as { current: boolean };

  return (
    <div
      role="button"
      tabIndex={0}
      draggable
      onDragStart={(e) => {
        e.stopPropagation();
        dragStarted.current = true;
        setDragPayload(e, { job_id: job.id, duration_minutes: dur, source_lane_id: sourceLaneId });
      }}
      onDragEnd={() => { setTimeout(() => { dragStarted.current = false; }, 50); }}
      onClick={() => { if (!dragStarted.current) navigate(`/jobs/${job.id}`); }}
      onKeyDown={(e) => { if (e.key === "Enter") navigate(`/jobs/${job.id}`); }}
      className={`absolute left-1 right-1 select-none rounded border px-2 py-1 text-xs shadow-sm transition hover:shadow ${colorClass}`}
      style={{
        top: startSlot * SLOT_PX,
        height: heightSlots * SLOT_PX - 2,
        cursor: "grab",
      }}
      title={`${job.job_number} — ${job.title}`}
    >
      <div className="font-mono text-[10px] opacity-70">{job.job_number}</div>
      <div className="truncate font-medium">{job.title}</div>
      {(job.address_line1 || job.city) && (
        <div className="truncate text-[10px] opacity-70">{[job.address_line1, job.city].filter(Boolean).join(", ")}</div>
      )}
    </div>
  );
}

function DraggableJob({ job, sourceLaneId }: { job: Job; sourceLaneId: string | "unassigned" }) {
  const navigate = useNavigate();
  const dragStarted = { current: false } as { current: boolean };
  return (
    <div
      role="button"
      tabIndex={0}
      draggable
      onDragStart={(e) => {
        e.stopPropagation();
        dragStarted.current = true;
        setDragPayload(e, {
          job_id: job.id,
          duration_minutes: jobDurationMinutes(job),
          source_lane_id: sourceLaneId,
        });
      }}
      onDragEnd={() => { setTimeout(() => { dragStarted.current = false; }, 50); }}
      onClick={() => { if (!dragStarted.current) navigate(`/jobs/${job.id}`); }}
      onKeyDown={(e) => { if (e.key === "Enter") navigate(`/jobs/${job.id}`); }}
      className="select-none rounded border border-slate-300 bg-white px-2 py-1 text-xs shadow-sm hover:border-brand-400 hover:shadow"
      style={{ cursor: "grab" }}
    >
      <div className="font-mono text-[10px] text-slate-500">{job.job_number}</div>
      <div className="font-medium">{job.title}</div>
      <div className="text-[10px] text-slate-500">{job.duration_estimate_minutes} min</div>
    </div>
  );
}
